import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import type { EnvironmentId, ScopedThreadRef } from "@amoreg/contracts";

import { useProjects } from "~/state/entities";
import { resolveFileWorkspaceRoot } from "~/components/files/fileWorkspaceRoot";

import { useHandleNewThread } from "./useHandleNewThread";

export interface ActiveProjectTarget {
  readonly environmentId: EnvironmentId;
  /** Null until the conversation has a durable workspace. Its organization
   * and thread still exist, so the cross-conversation catalog remains usable. */
  readonly cwd: string | null;
  readonly projectName: string;
  /** The project id — for Amoreg orgs, the organization id. */
  readonly projectId: string;
  readonly threadRef: ScopedThreadRef;
}

/**
 * Resolves the project workspace behind the active thread (or draft) so
 * project-scoped surfaces like the file picker and content search know which
 * workspace to query and which thread's right panel opens their results.
 */
export function useActiveProjectTarget(): ActiveProjectTarget | null {
  const { activeDraftThread, activeThread } = useHandleNewThread();
  const projects = useProjects();
  const thread = activeThread ?? activeDraftThread;
  const threadId = activeThread?.id ?? activeDraftThread?.threadId;
  const project = thread
    ? projects.find(
        (candidate) =>
          candidate.environmentId === thread.environmentId && candidate.id === thread.projectId,
      )
    : null;
  const cwd = resolveFileWorkspaceRoot(thread?.worktreePath, project?.workspaceRoot);

  if (!thread || !threadId || !project) return null;

  return {
    environmentId: project.environmentId,
    cwd,
    projectName: project.title,
    projectId: project.id,
    threadRef: scopeThreadRef(thread.environmentId, threadId),
  };
}
